import { useEffect } from 'react';
import { useState } from 'react';
import { TMealPaymentStrategy } from '@/common/integrations/menhir/employee/meal/meal.types';
import { useGetOperations } from '@/common/integrations/menhir/employee/operations/operations.hooks';
import { IOperation } from '@/common/integrations/menhir/employee/operations/operations.types';
import {
  STORAGE_OPTIONS,
  getStorageItem,
  setStorageItem,
} from '@/common/integrations/storage/storage.service';
import { parseDate } from '@/common/utils/date';
import { useCurrentMealPaymentStrategy } from '@/common/utils/meal-voucher';
import { differenceInDays, formatISO, startOfToday } from 'date-fns';

export type TSinglePaymentModalType =
  | 'failed_payment_with_credits'
  | 'failed_payment_no_strategy'
  | 'buoni_pasto_top_up'
  | 'with_credits'
  | 'no_strategy';

export async function shouldShowModalSinceLastView() {
  const today = startOfToday();
  const lastView = await getStorageItem(
    STORAGE_OPTIONS.SINGLE_MEAL_PAYMENT_MODAL_LAST_VIEW
  );

  if (!lastView) {
    return true;
  }

  const lastViewDate = parseDate(lastView);
  const diffDays = differenceInDays(today, lastViewDate);

  return diffDays >= 15;
}

export async function updateModalLastView() {
  const today = startOfToday();
  await setStorageItem(
    STORAGE_OPTIONS.SINGLE_MEAL_PAYMENT_MODAL_LAST_VIEW,
    formatISO(today)
  );
}

export function useShouldShowModal() {
  const { strategy, isLoading: isLoadingStrategy } =
    useCurrentMealPaymentStrategy();
  const { operations, isLoading: isLoadingOperations } = useGetOperations({
    page: 1,
    perPage: 5,
  });
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const checkForModalVisibility = async () => {
      const shouldShow = await shouldShowModalSinceLastView();
      setShouldShow(shouldShow);
    };
    checkForModalVisibility();
  }, []);

  if (isLoadingStrategy || isLoadingOperations) {
    return { shouldShow: false, modalType: null };
  }

  if (!strategy || strategy === 'card') {
    return { shouldShow: false, modalType: null };
  }

  const modalType = getModalType(strategy, operations?.list ?? []);
  const merchantName = getMerchantName(operations?.list ?? []);
  return { shouldShow, modalType, merchantName };
}

function getModalType(
  strategy: TMealPaymentStrategy,
  operations: IOperation[]
): TSinglePaymentModalType | null {
  const lastFailedPaymentReasons = getLastFailedPaymentReasons(operations);
  const hasNoBalanceReasons = lastFailedPaymentReasons.some(
    (reason) =>
      reason === 'not_enough_balance' || reason === 'max_vouchers_limit_reached'
  );
  const hasExactMatchReasons = lastFailedPaymentReasons.some(
    (reason) => reason === 'exact_match_not_possible'
  );

  if (strategy === 'none') {
    if (hasExactMatchReasons) {
      return 'failed_payment_no_strategy';
    }

    if (hasBuoniPastoTopUp(operations)) {
      return 'buoni_pasto_top_up';
    }

    return 'no_strategy';
  }

  if (strategy === 'credits') {
    if (hasNoBalanceReasons) {
      return 'failed_payment_with_credits';
    }

    return 'with_credits';
  }

  return null;
}

function getMerchantName(operations: IOperation[]): string | null {
  for (const operation of operations) {
    if (
      operation.type === 'benefit_expense' &&
      operation.status === 'rejected'
    ) {
      const merchantName = operation.descriptionParams.find(
        (param) => param.key === 'merchant_name'
      )?.value;

      if (merchantName) {
        return merchantName as string;
      }
    }
  }
  return null;
}

function hasBuoniPastoTopUp(operations: IOperation[]) {
  return operations.some(
    (operation) =>
      operation.type === 'topup' &&
      operation.descriptionParams.some(
        (param) => param.key === 'voucher_type' && param.value === 'meal'
      )
  );
}

function getLastFailedPaymentReasons(operations: IOperation[]) {
  const failedOperations = operations.filter(
    (operation) =>
      operation.type === 'benefit_expense' &&
      operation.status === 'rejected' &&
      operation.descriptionParams.some(
        (param) => param.key === 'rejection_reason'
      )
  );

  return failedOperations
    .map((operation) => {
      const rejectionReason = operation.descriptionParams.find(
        (param) => param.key === 'rejection_reason'
      )?.value;

      return rejectionReason;
    })
    .filter(Boolean);
}
